import i18n from "src/locales";
import { captureException } from "src/utils/error";
import saveToBraze from "src/utils/brazeUploader";
import getStore from "src/utils/redux/store";
import { trackEvent } from "src/utils/api/tracker";
import { isTelex295OrPseudoActive } from "src/utils/userSegment/features";
import { updatePaymentEnrollmentFormState } from "src/utils/redux/slices/paymentEnrollmentForm";

export interface SmsConsentUserPayload {
  phoneNumber: string;
  consentForTelehealthSms: boolean;
  consentForMarketingSms: boolean;
}

// Shape handed to crm-uploader-lambda's ATTRIBUTE_MAP.
export function buildSmsConsentUserPayload(input: {
  phoneNumber: string | undefined;
  marketingSmsConsent: boolean | undefined;
}): SmsConsentUserPayload {
  return {
    phoneNumber: (input.phoneNumber || "").trim(),
    consentForTelehealthSms: true,
    consentForMarketingSms: !!input.marketingSmsConsent,
  };
}

// Records the user's current SMS consent state across Redux, Iterable, and
// Mixpanel. Gated on isTelex295OrPseudoActive. Called on Continue past the consent
// disclaimer (captures consent for users who abandon before signup) and on
// OnSignedUp (reflects final state). Both read live Redux, so the later call
// always wins.
export function maybeRecordSmsConsent(): void {
  if (!isTelex295OrPseudoActive()) return;
  try {
    const store = getStore();
    const enrollmentInfo =
      store.getState()?.paymentEnrollmentForm?.enrollmentInfo;
    if (!enrollmentInfo?.phoneNumber) return;

    if (!enrollmentInfo.smsConsent) {
      store.dispatch(
        updatePaymentEnrollmentFormState({
          enrollmentInfo: { ...enrollmentInfo, smsConsent: true },
        })
      );
    }

    const user = buildSmsConsentUserPayload({
      phoneNumber: enrollmentInfo.phoneNumber,
      marketingSmsConsent: enrollmentInfo.marketingSmsConsent,
    });

    saveToBraze({ user });

    trackEvent("SuccessfullySavedSmsConsentForUser", {
      copy: i18n.t("payment:smsTransactionalDisclaimer"),
      consentType: "transactional",
    });

    if (user.consentForMarketingSms) {
      trackEvent("SuccessfullySavedSmsConsentForUser", {
        copy: i18n.t("enrollmentForm:inputFields:marketingSmsConsent:label"),
        consentType: "marketing",
      });
    }
  } catch (e) {
    captureException(e);
  }
}
